<template>
  <div class="app-container min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
    <Toast ref="toast" />
    <LoadingSpinner :loading="globalLoading" message="Loading..." />
    <div v-if="authStore.isAuthenticated && showSidebar" class="flex min-h-screen">
      <Sidebar />
      <div class="flex-1">
        <router-view />
      </div>
    </div>
    <div v-else>
      <router-view />
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed, ref, provide } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from './stores/auth';
import Sidebar from './components/Sidebar.vue';
import Toast from './components/Toast.vue';
import LoadingSpinner from './components/LoadingSpinner.vue';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const toast = ref(null);
const globalLoading = ref(false);

// Determine whether to show the sidebar based on the current route
const showSidebar = computed(() => {
  // List of routes where sidebar should be shown
  const sidebarRoutes = ['dashboard', 'property-list', 'analytics', 'data-import', 'users', 'settings'];
  return sidebarRoutes.includes(route.name);
});

onMounted(() => {
  // Check if token exists in localStorage and validate it
  authStore.checkAuth();
  
  // Apply any saved settings
  applySavedSettings();
});

// Apply settings from localStorage
const applySavedSettings = () => {
  const savedSettings = localStorage.getItem('user_settings');
  if (savedSettings) {
    try {
      const settings = JSON.parse(savedSettings);
      
      // Apply theme
      if (settings.theme === 'dark' || 
          (settings.theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      
      // Apply font settings if available
      if (settings.fontSize) {
        document.documentElement.style.setProperty('--font-size-base', `${settings.fontSize}px`);
      }
      
      if (settings.fontFamily) {
        document.documentElement.style.setProperty('--font-family', settings.fontFamily);
      }
    } catch (err) {
      console.error('Error parsing saved settings:', err);
    }
  }
};

// Provide toast and loading state to all components
provide('toast', toast);
provide('globalLoading', globalLoading);
</script>

<style>
.app-container {
  font-family: var(--font-family, 'Inter', sans-serif);
  font-size: var(--font-size-base, 16px);
}

/* High contrast mode */
.high-contrast {
  --bg-primary: #000000;
  --text-primary: #ffffff;
  --border-primary: #ffffff;
}

/* Reduced motion */
.reduce-motion * {
  transition: none !important;
  animation: none !important;
}
</style>
